/* REACT */
import { useState, useRef, useEffect } from "react";

/* ROUTER */
import { useNavigate, useLocation } from "react-router-dom";

/* PLUGINS */
import { Bell, User, LogOut, Lock } from "lucide-react";

/* ASSETS */
import idlLogo from "@/assets/idl-logo-header.png";

/* COMPONENTS */
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ExitKioskDialog } from "@/features/attendance/components/ExitKioskDialog";

/* STORE */
import { useAuthStore } from "@/store/authStore";
import { useUiStore } from "@/store/uiStore";

/* HOOKS */
import { useLogout } from "@/features/auth";
/* CONTEXTS */
import { useUnsavedChangesContext } from "@/contexts/UnsavedChangesContext";

interface TopNavProps {
  contentPaddingClasses: string;
}

export const TopNav = ({ contentPaddingClasses }: TopNavProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasUnsavedChanges, setPendingNavigation } = useUnsavedChangesContext();
  const { user, kioskMode } = useAuthStore();
  const { notifications } = useUiStore();
  const { mutate: logout, isPending } = useLogout();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showExitKioskDialog, setShowExitKioskDialog] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout(undefined, {
      onSettled: () => {
        setShowLogoutDialog(false);
      },
    });
  };

  if(kioskMode){
    return (
      <nav
        className="bg-auth-gradient-from text-auth-text border-b border-border sticky top-0 z-50"
        role="navigation"
        aria-label="Attendance kiosk navigation"
      >
        <div className={contentPaddingClasses}>
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between h-[63px]">
              <div className="inline-flex items-center">
                <img src={idlLogo} alt="IDL Projects" className="h-10 w-auto" />
              </div>
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-auth-gradient-to text-auth-text text-sm font-medium"
                  aria-label="Attendance mode active"
                >
                  <Lock className="w-4 h-4" aria-hidden="true" />
                  Attendance Mode
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowExitKioskDialog(true)}
                  className="bg-auth-gradient-to text-auth-text border-auth-gradient-to hover:bg-brand hover:text-brand-foreground hover:border-brand"
                >
                  Exit Kiosk
                </Button>
              </div>
            </div>
          </div>
        </div>
        <ExitKioskDialog open={showExitKioskDialog} onOpenChange={setShowExitKioskDialog} />
      </nav>
    );
  }

  return (
    <nav
      className="bg-auth-gradient-from text-auth-text border-b border-border sticky top-0 z-40"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className={`${contentPaddingClasses} transition-all duration-300`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-end h-[63px]">
            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <button
                className="relative p-2 hover:bg-auth-gradient-to rounded-lg transition-colors"
                aria-label={`Notifications${notifications > 0 ? `, ${notifications} unread` : ""}`}
              >
                <Bell className="w-6 h-6" aria-hidden="true" />
                {notifications > 0 && (
                  <span
                    className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center"
                    aria-hidden="true"
                  >
                    {notifications}
                  </span>
                )}
              </button>

              {/* Profile */}
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={() => setShowProfileMenu((prev) => !prev)}
                  className="flex items-center gap-2 p-2 hover:bg-auth-gradient-to rounded-lg transition-colors"
                  aria-label="User menu"
                  aria-haspopup="true"
                  aria-expanded={showProfileMenu}
                >
                  <div className="w-8 h-8 bg-brand rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-brand-foreground">
                      {user?.first_name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </button>

                {/* Dropdown */}
                {showProfileMenu && (
                  <div
                    className="absolute right-0 mt-2 w-56 bg-popover border border-border rounded-lg shadow-xl overflow-hidden z-50"
                    role="menu"
                  >
                    {/* User Info Header */}
                    <div className="px-4 py-3 border-b border-border">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-brand rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-base font-semibold text-brand-foreground">
                            {user?.first_name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-popover-foreground truncate">
                            {user?.first_name} {user?.last_name}
                          </p>
                          <p className="text-sm text-muted-foreground truncate">
                            {user?.email_address}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowProfileMenu(false);
                          const targetPath = "/settings";
                          if (hasUnsavedChanges && location.pathname !== targetPath) {
                            setPendingNavigation(targetPath);
                          } else {
                            navigate(targetPath);
                          }
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-popover-foreground hover:bg-accent transition-colors"
                        role="menuitem"
                      >
                        <User className="w-5 h-5 text-muted-foreground" />
                        <span>My Profile</span>
                      </button>
                    </div>

                    {/* Logout */}
                    <div className="border-t border-border py-1">
                      <button
                        onClick={() => {
                          setShowProfileMenu(false);
                          setShowLogoutDialog(true);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-destructive hover:bg-accent transition-colors"
                        role="menuitem"
                      >
                        <LogOut className="w-5 h-5" />
                        <span>Log Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Confirm Logout</DialogTitle>
            <DialogDescription>
              Are you sure you want to logout? Any unsaved changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowLogoutDialog(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleLogout}
              disabled={isPending}
            >
              {isPending ? "Logging out..." : "Logout"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </nav>
  );
};
